import React, { useState, useEffect } from "react";
import { Table, Button, Row, Col, Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { MdKeyboardArrowUp, MdKeyboardArrowDown } from "react-icons/md";
import { RxCaretSort } from "react-icons/rx";
import { BsEye, BsThreeDotsVertical, BsTrash3, BsArrowRight } from "react-icons/bs";
import { GoCircleSlash } from "react-icons/go";
import { PiToggleRight } from "react-icons/pi";
import ImageNoDataFound from "../../images/nodata_found.gif";
import ReactPaginate from "react-paginate";
import InvestmentSchedulesFilterBlock from "./InvestmentSchedulesFilterBlock";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import apiConfig from "../credentials/apiConfig";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./index.scss";


const AllInvestmentSchedulesTable = ({ isModalOpen, onEditClick, isChecked }) => {
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "createdAt", direction: "desc" });
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [activeFilters, setActiveFilters] = useState({});

  const itemsPerPage = 10;
  const navigate = useNavigate();

  const fetchInvestmentSchedules = async (filters = {}, page = 0, sort = sortConfig) => {
    try {
      setLoading(true);

      // Prepare payload according to backend expectations
      const payload = {
        page: page + 1,
        perPage: itemsPerPage,
        sorting: {
          key: sort.key || "createdAt",
          order: sort.direction === "asc" ? 1 : -1
        },
        searchString: filters.search || "",
        filters: {
          clientName: filters.client && filters.client !== "All Clients" ? [filters.client] : [],
          analysisYear: filters.year && filters.year !== "All Years" ? [filters.year] : [],
          status: filters.status && filters.status !== "All Status" ? [filters.status] : []
        }
      };

      const response = await axios.post(
        `${apiConfig.apiBaseURL}/getAllInvestmentSchedulesWithClient`,
        payload,
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.success && response.data.data) {
        const schedules = response.data.data;

        // Format data for display - no client-side filtering needed
        const formatted = schedules.map((schedule) => ({
          id: schedule._id,
          clientId: schedule.clientId,
          clientName: schedule.clientName || "--",
          analysisYear: schedule.analysisYear || "--",
          totalInvestments: schedule.totalInvestments || "--",
          portfolioValue: schedule.portfolioValue || "--",
          marketValuation: schedule.marketValuation || "--",
          priorYearComparison: schedule.priorYearComparison || "--",
          status: schedule.status || "Pending",
          isReportGenerated: schedule.isReportGenerated || false,
          createdAt: schedule.createdAt,
          updatedAt: schedule.updatedAt,
          lastUpdated: new Date(schedule.updatedAt).toLocaleDateString() || "--"
        }));

        setData(formatted);
        setFilteredData(formatted);
        setTotalCount(response.data.totalCount || formatted.length);
      } else {
        setData([]);
        setFilteredData([]);
        setTotalCount(0);
      }

    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load investment schedules.");
      setData([]);
      setFilteredData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvestmentSchedules();
  }, [isModalOpen, isChecked]);

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? <MdKeyboardArrowUp size={20} /> : <MdKeyboardArrowDown size={20} />;
    }
    return <RxCaretSort size={20} />;
  };

  const sortTable = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    const newSort = { key, direction };
    setSortConfig(newSort);
    fetchInvestmentSchedules({}, currentPage, newSort);
  };

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
    fetchInvestmentSchedules(activeFilters, selected, sortConfig);
  };

  const handleFilterApply = (filters) => {
    setSearchValue(filters.search || "");
    setActiveFilters(filters);
    setCurrentPage(0);
    fetchInvestmentSchedules(filters, 0, sortConfig);
  };

  const handleScheduleClick = (scheduleId) => {
    navigate(`/investment-schedules/details/${scheduleId}`);
  };

  const handleEditAnalysis = (scheduleId) => {
    navigate(`/investment-schedules/edit/${scheduleId}`);
  };

  const getStatusBadge = (status) => (
    <span className="badge rounded px-3 py-2"
      style={{
        backgroundColor: status === "Completed" ? "#D9FBEC" : 
                         status === "In Progress" ? "#FFF3CD" : 
                         status === "Pending" ? "#FFF3CD" : "#FFEDED",
        color: status === "Completed" ? "#08A45D" : 
               status === "In Progress" ? "#856404" : 
               status === "Pending" ? "#856404" : "#FF0000",
        fontWeight: "700"
      }}>
      {status}
    </span>
  );

  const displayedData = data;

  return (
    <>
      <InvestmentSchedulesFilterBlock
        onApply={handleFilterApply}
      />

      <Table hover responsive style={{ border: "2px solid #E7ECF1" }} className="all-clients-table">
        <thead>
          <tr style={{ whiteSpace: "nowrap" }}>
            <th onClick={() => sortTable("clientName")} className="text-light py-3">
              Client {getSortIcon("clientName")}
            </th>
            <th onClick={() => sortTable("analysisYear")} className="text-light py-3">
              Analysis Year {getSortIcon("analysisYear")}
            </th>
            <th onClick={() => sortTable("totalInvestments")} className="text-light py-3">
              Total Investments {getSortIcon("totalInvestments")}
            </th>
            <th onClick={() => sortTable("portfolioValue")} className="text-light py-3">
              Portfolio Value {getSortIcon("portfolioValue")}
            </th>
            <th onClick={() => sortTable("priorYearComparison")} className="text-light py-3">
              Prior Year Comparison {getSortIcon("priorYearComparison")}
            </th>
            <th onClick={() => sortTable("marketValuation")} className="text-light py-3">
              Market Valuation {getSortIcon("marketValuation")}
            </th>
            <th onClick={() => sortTable("status")} className="text-light py-3">
              Status {getSortIcon("status")}
            </th>
            <th className="text-light py-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr style={{ whiteSpace: "nowrap" }}>
              <td colSpan="8" className="text-center align-middle">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </td>
            </tr>
          ) : displayedData.length > 0 ? (
            displayedData.map((row) => (
              <tr key={row.id} style={{ whiteSpace: "nowrap" }}>
                <td className="align-middle">
                  <div
                    onClick={() => handleScheduleClick(row.id)}
                    style={{
                      color: "#073E72",
                      fontWeight: "700",
                      cursor: "pointer",
                      textDecoration: "none"
                    }}
                    className="client-name-link"
                  >
                    {row.clientName}
                  </div>
                  <div style={{ color: "#6B7280", fontSize: "0.875rem" }}>{row.analysisYear}</div>
                </td>
                <td className="align-middle" style={{ color: "#6B7280" }}>{row.analysisYear}</td>
                <td className="align-middle" style={{ color: "#6B7280" }}>{row.totalInvestments}</td>
                <td className="align-middle" style={{ color: "#6B7280", fontWeight: 600 }}>{row.portfolioValue}</td>
                <td className="align-middle">
                  {row.priorYearComparison && row.priorYearComparison !== "--" ? (
                    (() => {
                      const match = row.priorYearComparison.match(/^([^()]+)\s+\(([^()]+)\)$/);
                      if (match) {
                        const value = match[1].trim();           // Main value
                        const change = match[2].trim();          // Change percentage
                        const isPositive = value.startsWith("+");

                        return (
                          <div>
                            <div style={{ fontWeight: 600 }}>{change}</div>
                            <div
                              className={isPositive ? "text-success" : ""}
                              style={{ fontSize: "0.875rem" }}
                            >
                              {value}
                            </div>
                          </div>
                        );
                      }
                      // Fallback for single value format
                      const numValue = Number(row.priorYearComparison);
                      return (
                        <span 
                          className={numValue >= 0 ? "text-success" : ""}
                          style={{ color: "#6B7280" }}
                        >
                          {numValue >= 0 ? "+" : ""}{row.priorYearComparison}
                        </span>
                      );
                    })()
                  ) : (
                    <span style={{ color: "#6B7280" }}>--</span>
                  )}
                </td>
                <td className="align-middle">
                  {row.marketValuation && row.marketValuation !== "--" ? (
                    (() => {
                      const match = row.marketValuation.match(/^([^()]+)\s+\(([^()]+)\)$/);
                      if (match) {
                        const value = match[1].trim();           // "$28,500,216"
                        const change = match[2].trim();          // "+17.5%"
                        const isPositive = change.startsWith("+");

                        return (
                          <div>
                            <div style={{ fontWeight: 600 }}>{value}</div>
                            <div
                              className={isPositive ? "text-success" : ""}
                              style={{ fontSize: "0.875rem" }}
                            >
                              {change}
                            </div>
                          </div>
                        );
                      }
                      return <span>{row.marketValuation}</span>; // fallback if format doesn't match
                    })()
                  ) : (
                    <span style={{ color: "#6B7280" }}>--</span>
                  )}
                </td>

                <td className="align-middle">{getStatusBadge(row.status)}</td>
                <td className="align-middle">
                  <div className="d-flex">
                    {row.status === "In Progress" ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`tooltip-edit-${row.id}`}>Edit Analysis</Tooltip>}
                      >
                        <Button
                          variant="link"
                          className="p-1 me-2 bg-transparent d-flex align-items-center justify-content-center"
                          onClick={() => handleEditAnalysis(row.id)}
                          style={{
                            width: "28px",
                            height: "28px",
                            borderRadius: "50%",
                            border: "1px solid #6B7280"
                          }}
                        >
                          <BsArrowRight size={12} color="#6B7280" />
                        </Button>
                      </OverlayTrigger>
                    ) : (
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`tooltip-view-${row.id}`}>View Schedule</Tooltip>}
                      >
                        <Button
                          variant="link"
                          className="p-1 me-2 bg-transparent"
                          onClick={() => handleScheduleClick(row.id)}
                        >
                          <BsEye size={25} color="#6B7280" />
                        </Button>
                      </OverlayTrigger>
                    )}
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="8" className="text-center py-5">
                <div>
                  <img src={ImageNoDataFound} alt="No data found" style={{ maxHeight: "150px" }} />
                  <p className="fw-bold text-dark mb-0 mt-3">No data found</p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {filteredData.length > 0 && (
        <Row className="mt-3 mb-4">
          <Col md={6}>
            <p style={{ color: "#6B7280" }}>
              Showing: {totalCount === 0 ? 0 : currentPage * itemsPerPage + 1} – {Math.min((currentPage + 1) * itemsPerPage, totalCount)} of {totalCount}
            </p>
          </Col>

          <Col md={6} className="d-flex justify-content-end">
            <ReactPaginate
              previousLabel={"<"}
              nextLabel={">"}
              breakLabel="..."
              pageCount={Math.ceil(totalCount / itemsPerPage)}
              marginPagesDisplayed={2}
              pageRangeDisplayed={2}
              onPageChange={handlePageClick}
              containerClassName={"pagination"}
              activeClassName={"active"}
              forcePage={currentPage}
            />
          </Col>
        </Row>
      )}
    </>
  );
}

export default AllInvestmentSchedulesTable;